import { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Grid, TextField,
  InputAdornment, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, IconButton, Select, useTheme,
  Avatar, Stack, TablePagination,
} from "@mui/material";
import { tokens } from "../../theme";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useNavigate } from "react-router-dom";
import apiService from "../../service/Api";

type ApplicationStatus = "draft" | "submitted" | "under_review" | "approved" | "rejected";

interface ApiOrganiser {
  lead_name: string;
  org_name: string;
  email: string | null;
  phone_number: string | null;
}

interface ApiApplication {
  id: number;
  reference_id: string;
  organiser: ApiOrganiser;
  conference_title: string;
  conference_start_date: string;
  conference_end_date: string | null;
  attendees: number | null;
  venue_name: string;
  status: ApplicationStatus;
  created_at: string;
}

interface TableRowData {
  id: string;
  title: string;
  date: string;
  endDate: string | null;
  organiser: string;
  orgName: string;
  contact: string;
  attendees: number;
  location: string;
  status: ApplicationStatus;
  submitted: string;
}

interface ApiSummary {
  total: number;
  pending: number;
  approved: number;
  declined: number;
}

const getStatusStyles = (status: ApplicationStatus) => {
  switch (status) {
    case "submitted":   return { bgcolor: "#F79E1B22", color: "#F79E1B", label: "Submitted" };
    case "approved":    return { bgcolor: "#4BA66322", color: "#4BA663", label: "Approved" };
    case "under_review":return { bgcolor: "#3B82F622", color: "#3B82F6", label: "In Review" };
    case "rejected":    return { bgcolor: "#EB433522", color: "#EB4335", label: "Rejected" };
    case "draft":       return { bgcolor: "#E5E7EB",   color: "#6B7280", label: "Draft" };
    default:            return { bgcolor: "#E5E7EB",   color: "#6B7280", label: status };
  }
};

const getInitials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

export default function StreamFundDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const cardBg    = colors.primary[900];
  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textMuted   = colors.primary[300];

  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder]     = useState("newest");
  const [rows, setRows]               = useState<TableRowData[]>([]);
  const [loading, setLoading]         = useState(false);
  const [page, setPage]               = useState(1);
  const [totalCount, setTotalCount]   = useState(0);
  const [summary, setSummary]         = useState<ApiSummary>({ total: 0, pending: 0, approved: 0, declined: 0 });

  const PAGE_SIZE = 10;

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sortOrder]);

  useEffect(() => {
    apiService
      .get<{ count: number; results: ApiApplication[] }>(
        "/api/streamfund/applications/",
        { page_size: "1000" },
      )
      .then((res) => {
        const all = res.data.results ?? [];
        setSummary({
          total:    res.data.count,
          pending:  all.filter((a) => a.status === "submitted").length,
          approved: all.filter((a) => a.status === "approved").length,
          declined: all.filter((a) => a.status === "rejected").length,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params: Record<string, string> = {};
    if (search.trim()) params.search = search.trim();
    if (statusFilter !== "all") params.status = statusFilter;
    params.ordering = sortOrder === "newest" ? "-created_at" : "created_at";
    params.page = String(page);

    apiService
      .get<{ count: number; next: string | null; results: ApiApplication[] }>(
        "/api/streamfund/applications/",
        params,
      )
      .then((response) => {
        if (cancelled) return;
        const { count, results } = response.data;
        setTotalCount(count);
        setRows(
          results.map((app) => ({
            id:        app.reference_id,
            title:     app.conference_title,
            date:      app.conference_start_date,
            endDate:   app.conference_end_date,
            organiser: app.organiser?.lead_name ?? "—",
            orgName:   app.organiser?.org_name ?? "",
            contact:   app.organiser?.phone_number || app.organiser?.email || "",
            attendees: app.attendees ?? 0,
            location:  app.venue_name,
            status:    app.status,
            submitted: app.created_at,
          }))
        );
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [search, statusFilter, sortOrder, page]);

  const statCards = [
    { label: "Total Applications", value: summary.total,    icon: <VideoLibraryOutlinedIcon sx={{ fontSize: 22 }} />,      iconBg: "#3B82F622", iconColor: "#3B82F6" },
    { label: "Pending Review",     value: summary.pending,  icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 22 }} />,     iconBg: "#F79E1B22", iconColor: "#F79E1B" },
    { label: "Approved",           value: summary.approved, icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 22 }} />, iconBg: "#4BA66322", iconColor: "#4BA663" },
    { label: "Declined",           value: summary.declined, icon: <CancelOutlinedIcon sx={{ fontSize: 22 }} />,            iconBg: "#EB433522", iconColor: "#EB4335" },
  ];

  return (
    <Box>
      <Grid container spacing={2.5} mb={3}>
        {statCards.map((card, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
            <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "16px", border: `1px solid ${colors.primary[300]}18` }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: "20px !important" }}>
                <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: card.iconBg, color: card.iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {card.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, color: textMuted, fontWeight: 500, mb: 0.3 }}>{card.label}</Typography>
                  <Typography sx={{ fontSize: 26, fontWeight: 700, color: textPrimary, lineHeight: 1 }}>{card.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px", border: `1px solid ${colors.primary[300]}18` }}>
        <CardContent sx={{ p: "24px !important" }}>
          {/* FILTERS */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3} alignItems={{ xs: "stretch", sm: "center" }}>
            <TextField
              size="small"
              placeholder="Search by title, organiser or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, maxWidth: { sm: 380 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
            <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ borderRadius: "10px", minWidth: 140, color: textMuted }}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="under_review">In Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
            <Select size="small" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} sx={{ borderRadius: "10px", minWidth: 130, color: textMuted }}>
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
            </Select>
          </Stack>

          {/* TABLE */}
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead sx={{ bgcolor: mutedCard }}>
                <TableRow>
                  {["Event", "Date", "Organiser", "Attendees", "Location", "Status", "Submitted", ""].map((col) => (
                    <TableCell key={col} sx={{ fontSize: 11, fontWeight: 700, color: textMuted, textTransform: "uppercase", letterSpacing: 0.5, py: 1.5, whiteSpace: "nowrap", "&:first-of-type": { borderRadius: "10px 0 0 10px" }, "&:last-of-type": { borderRadius: "0 10px 10px 0" } }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => {
                  const statusStyle = getStatusStyles(row.status);
                  return (
                    <TableRow key={row.id} hover onClick={() => navigate(`/streamfund/applications/${row.id}`)} sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: textPrimary, maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {row.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: textMuted, mt: 0.3 }}>{row.id}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: 13, color: textPrimary }}>
                          {new Date(row.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </Typography>
                        {row.endDate && (
                          <Typography sx={{ fontSize: 11, color: textMuted, mt: 0.2 }}>
                            to {new Date(row.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: "#0F4D92", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                            {getInitials(row.organiser)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: textPrimary, lineHeight: 1.2 }}>{row.organiser}</Typography>
                            <Typography sx={{ fontSize: 11, color: textMuted }}>{row.orgName}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: textPrimary, fontWeight: 600 }}>
                          {row.attendees.toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: textPrimary, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {row.location}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip label={statusStyle.label} size="small" sx={{ bgcolor: statusStyle.bgcolor, color: statusStyle.color, borderRadius: "8px", height: 26, fontWeight: 600, fontSize: 11, "& .MuiChip-label": { px: 1.5 } }} />
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: 12, color: textMuted }}>
                          {new Date(row.submitted).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </Typography>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ pr: 1 }}>
                        <IconButton size="small" sx={{ color: textMuted }}>
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: textMuted, fontSize: 14 }}>
                        {loading ? "Loading…" : "No applications found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={totalCount}
            page={page - 1}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[]}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            sx={{ borderTop: `1px solid ${colors.primary[300]}18`, mt: 1 }}
          />
        </CardContent>
      </Card>
    </Box>
  );
}